import { FC, useEffect, useState } from 'react'
import moment from 'moment'
import { RenderIf } from '../RenderIf'
import { Button } from '../Button'

import { handleLocalStorage } from '../../featureSliced/shared/utils/handleLocalStorage'
import { useTranslations } from '../../featureSliced/shared/hooks/useTranslations'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { GridDiv } from '../../featureSliced/shared/utils/gridDiv'

import { atcb_action } from 'add-to-calendar-button'

import {
  ReceiptModalContainer,
  MessageContainerNoReceipt,
  ButtonContainer,
} from './styled'
import { useSearchParams } from 'react-router-dom'
import { clickCollectPOST } from 'featureSliced/shared/api/clickCollect/POST'
import { ReceiptLoading } from './ReceiptLoading'
import { ReceiptError } from './ReceiptError'
import { AnimationContainer } from './SuccessLayouts/styled'
import Lottie from 'lottie-react'
import { AnimationData } from 'featureSliced/shared/utils/animations'
import { loadAnimation } from 'featureSliced/shared/utils/loadAnimation'
import { PaymentFailed } from './PaymentFailed'

type NoReceiptModalProps = {
  paymentNumberProps?: string
  onCloseModal: () => void
}

type IStatus = 'isLoading' | 'success' | 'error' | 'paymentError'

const PICKUP_EVENT_MINUTES = 15

export const NoReceiptModal: FC<NoReceiptModalProps> = ({
  paymentNumberProps,
  onCloseModal,
}) => {
  // Hooks
  const t = useTranslations()

  // Search params
  const [searchParams] = useSearchParams()
  const datatransTrxId = searchParams.get('datatransTrxId')
  const order_guid_param = searchParams.get('order_guid')
  const orderGuid_param = searchParams.get('orderGuid')

  // Store
  const { restaurants } = useMySelector((state) => state)

  const { CompanyName, ContractorName } = useMySelector(
    (state) => state.app.companyData
  )
  const { selectedLanguage } = useMySelector((state) => state.common)
  const { appTheme } = useMySelector((state) => state.app.interfaceStyles)
  const { pickUpTime, registerId } = handleLocalStorage([
    'pickUpTime',
    'registerId',
  ])

  // State
  const [status, setStatus] = useState<IStatus>('isLoading')
  const [orderGuid, setOrderGuid] = useState<string>()

  const [payment_success_nr, setPayment_success_nr] = useState('')
  const [isShowCalendarButton, seIsShowCalendarButton] = useState(true)

  // useEffects
  useEffect(() => {
    if (order_guid_param || orderGuid_param) {
      setOrderGuid(order_guid_param || (orderGuid_param as string))
    }
  }, [order_guid_param, orderGuid_param])

  useEffect(() => {
    if (pickUpTime) {
      seIsShowCalendarButton(true)
    } else {
      seIsShowCalendarButton(false)
    }
  }, [pickUpTime])

  useEffect(() => {
    if (paymentNumberProps) {
      setPayment_success_nr(paymentNumberProps)
      setStatus('success')
    }
    if (!paymentNumberProps) {
      triggerPaymentComplete()
    }
  }, [paymentNumberProps, orderGuid])

  // Functions
  const triggerPaymentComplete = async () => {
    if (datatransTrxId && orderGuid) {
      try {
        const clickResult = await clickCollectPOST.paymentComplete(
          datatransTrxId,
          orderGuid
        )
        if (clickResult?.orderNr) {
          setPayment_success_nr(clickResult.orderNr)
          setStatus('success')
        } else {
          setStatus('paymentError')
        }
      } catch (e) {
        setStatus('error')
      }
    }
  }

  const addToCalendar = () => {
    const start = moment(pickUpTime)
    const end = start.clone().add(PICKUP_EVENT_MINUTES, 'minutes')
    const address =
      restaurants?.find((rest) => {
        return rest?.clickAndCollectClientId === registerId
      })?.address || ''
    const eventText = {
      orderNumber: payment_success_nr,
      companyName: CompanyName || ContractorName,
      time: start.format('HH:mm'),
    }
    const config = {
      label: `${CompanyName} order`,
      name: t('calendar.eventTitle', eventText),
      description: t('calendar.eventDescription', eventText),
      location: address,

      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
      startTime: start.format('HH:mm'),
      endTime: end.format('HH:mm'),

      options: ['Apple', 'Google', 'Yahoo', 'Microsoft365'] as any,

      lightMode: appTheme,
      language: selectedLanguage,
      timeZone: 'Europe/Zurich',
    }
    atcb_action(config)
  }

  // State
  const [animationJson, setAnimationJson] = useState<AnimationData | null>(null)

  // useEffect
  useEffect(() => {
    const animationPath = `${appTheme}/smileyDefault.json`
    loadAnimation(animationPath, (error) => {
      console.error('Failed to load smiley animation:', error)
    }).then(setAnimationJson)
  }, [])

  return (
    <>
      <RenderIf condition={status === 'isLoading'}>
        <ReceiptLoading />
      </RenderIf>
      <RenderIf condition={status === 'error'}>
        <ReceiptError onCloseModal={onCloseModal} />
      </RenderIf>
      <RenderIf condition={status === 'paymentError'}>
        <PaymentFailed onCloseModal={onCloseModal} />
      </RenderIf>
      <RenderIf condition={status === 'success'}>
        <ReceiptModalContainer>
          <GridDiv gc="1 / -1">
            <MessageContainerNoReceipt>
              <h2>
                {t('receipt.paymentSuccessOrderNumber', {
                  orderNumber: payment_success_nr,
                })}
              </h2>
            </MessageContainerNoReceipt>
          </GridDiv>

          <AnimationContainer>
            <Lottie animationData={animationJson} loop={true} />
          </AnimationContainer>

          <GridDiv gc="1 / -1">
            <RenderIf condition={!isShowCalendarButton}>
              <Button buttonType="stroke" width="100%" onClick={onCloseModal}>
                {t('buttons.ok')}
              </Button>
            </RenderIf>
            <RenderIf condition={isShowCalendarButton}>
              <ButtonContainer>
                <Button buttonType="stroke" width="100%" onClick={onCloseModal}>
                  {t('buttons.close')}
                </Button>
                <Button
                  buttonType="common"
                  width="100%"
                  onClick={addToCalendar}
                >
                  {t('buttons.addToCalendar')}
                </Button>
              </ButtonContainer>
            </RenderIf>
          </GridDiv>
        </ReceiptModalContainer>
      </RenderIf>
    </>
  )
}
